import { useState } from 'react';
import { AdminLayout } from '../components/Layout/AdminLayout';
import { PageHeader, StatCard } from '../components/common';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  CircularProgress,
  Alert,
  Typography,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  Collapse,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  FileDownload,
  PictureAsPdf,
  CalendarToday,
  Receipt,
  TrendingUp,
  CheckCircle,
  Cancel,
  HourglassEmpty,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import { useTransactionReport, useDownloadTransactionReport } from '../hooks';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { colors, transitions, radius } from '../theme/theme';

const CHART_COLORS = [
  colors.success.main,
  colors.warning.main,
  colors.error.main,
  colors.info.main,
];

export const TransactionsReportPage = () => {
  const [period, setPeriod] = useState('this_month');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [status, setStatus] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');

  // Calculate dates based on selected period
  const getDateRange = () => {
    const now = new Date();
    let start = '';
    let end = '';

    switch (period) {
      case 'today':
        start = end = now.toISOString().split('T')[0];
        break;
      case 'yesterday':
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        start = end = yesterday.toISOString().split('T')[0];
        break;
      case 'this_week':
        const weekStart = new Date(now);
        weekStart.setDate(now.getDate() - now.getDay());
        start = weekStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_week':
        const lastWeekStart = new Date(now);
        lastWeekStart.setDate(now.getDate() - now.getDay() - 7);
        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        start = lastWeekStart.toISOString().split('T')[0];
        end = lastWeekEnd.toISOString().split('T')[0];
        break;
      case 'this_month':
        const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
        start = monthStart.toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
        break;
      case 'last_month':
        const lastMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        start = lastMonthStart.toISOString().split('T')[0];
        end = lastMonthEnd.toISOString().split('T')[0];
        break;
      case 'custom':
        start = startDate;
        end = endDate;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0];
        end = now.toISOString().split('T')[0];
    }

    return { start, end };
  };

  const dateRange = getDateRange();
  const shouldFetch = period !== 'custom' || (!!startDate && !!endDate);

  const { report, isLoading, error } = useTransactionReport(
    {
      start_date: dateRange.start,
      end_date: dateRange.end,
      status: status || undefined,
      payment_method: paymentMethod || undefined,
    },
    shouldFetch
  );

  const { downloadReport, isDownloading } = useDownloadTransactionReport();

  const handleDownload = async (format: 'pdf' | 'excel' | 'csv') => {
    await downloadReport({
      start_date: dateRange.start,
      end_date: dateRange.end,
      status: status || undefined,
      payment_method: paymentMethod || undefined,
      format,
    });
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'successful':
        return 'success';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'successful':
        return <CheckCircle sx={{ fontSize: 16 }} />;
      case 'pending':
        return <HourglassEmpty sx={{ fontSize: 16 }} />;
      case 'failed':
        return <Cancel sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  return (
    <AdminLayout>
      <Box>
        <PageHeader
          title="Transactions Report"
          subtitle="Comprehensive transaction analysis and monitoring"
          breadcrumbs={[
            { label: 'Dashboard', path: '/dashboard' },
            { label: 'Reports' },
            { label: 'Transactions Report' },
          ]}
          action={
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                startIcon={<FileDownload />}
                onClick={() => handleDownload('excel')}
                disabled={isDownloading || !shouldFetch}
                size="small"
              >
                Excel
              </Button>
              <Button
                variant="outlined"
                startIcon={<PictureAsPdf />}
                onClick={() => handleDownload('pdf')}
                disabled={isDownloading || !shouldFetch}
                size="small"
              >
                PDF
              </Button>
            </Box>
          }
        />

        {/* Period Selector & Filters */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Time Period */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ color: colors.primary.main }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Time Period
                    </Typography>
                  </Box>
                  <ToggleButtonGroup
                    value={period}
                    exclusive
                    onChange={(e, newPeriod) => newPeriod && setPeriod(newPeriod)}
                    size="small"
                    sx={{
                      flexWrap: 'wrap',
                      '& .MuiToggleButton-root': {
                        textTransform: 'none',
                        fontWeight: 500,
                        px: 2,
                      },
                      '& .Mui-selected': {
                        bgcolor: `${colors.primary.main} !important`,
                        color: 'white !important',
                      },
                    }}
                  >
                    <ToggleButton value="today">Today</ToggleButton>
                    <ToggleButton value="yesterday">Yesterday</ToggleButton>
                    <ToggleButton value="this_week">This Week</ToggleButton>
                    <ToggleButton value="last_week">Last Week</ToggleButton>
                    <ToggleButton value="this_month">This Month</ToggleButton>
                    <ToggleButton value="last_month">Last Month</ToggleButton>
                    <ToggleButton value="custom">Custom</ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Collapse in={period === 'custom'}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Start Date"
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="End Date"
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        size="small"
                      />
                    </Grid>
                  </Grid>
                </Collapse>
              </Box>

              <Divider />

              {/* Additional Filters */}
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                  Filters
                </Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={status} onChange={(e) => setStatus(e.target.value)} label="Status">
                        <MenuItem value="">All Statuses</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Payment Method</InputLabel>
                      <Select
                        value={paymentMethod}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        label="Payment Method"
                      >
                        <MenuItem value="">All Methods</MenuItem>
                        <MenuItem value="mpesa">M-Pesa</MenuItem>
                        <MenuItem value="bank">Bank Transfer</MenuItem>
                        <MenuItem value="cash">Cash</MenuItem>
                        <MenuItem value="card">Card</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load transactions report. Please try again.
          </Alert>
        )}

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress />
          </Box>
        ) : report ? (
          <>
            {/* Summary Stats */}
            <Grid container spacing={3} mb={3}>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Total Transactions"
                  value={report.total_transactions?.toLocaleString() || 0}
                  icon={<Receipt />}
                  color={colors.primary.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Successful"
                  value={report.by_status?.completed?.count?.toLocaleString() || 0}
                  icon={<CheckCircle />}
                  color={colors.success.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Pending"
                  value={report.by_status?.pending?.count?.toLocaleString() || 0}
                  icon={<HourglassEmpty />}
                  color={colors.warning.main}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <StatCard
                  title="Failed"
                  value={report.by_status?.failed?.count?.toLocaleString() || 0}
                  icon={<Cancel />}
                  color={colors.error.main}
                />
              </Grid>
            </Grid>

            {/* Charts Section */}
            <Grid container spacing={3} mb={3}>
              {/* Status Distribution */}
              {report.by_status && Object.keys(report.by_status).length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Transaction Status Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={Object.entries(report.by_status).map(([status, data]: [string, any]) => ({
                              name: status.charAt(0).toUpperCase() + status.slice(1),
                              value: data.count || 0,
                            }))}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={100}
                            label={(entry: any) => `${entry.name}: ${entry.value}`}
                          >
                            {Object.keys(report.by_status).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}

              {/* Payment Method Distribution */}
              {report.by_payment_method && Object.keys(report.by_payment_method).length > 0 && (
                <Grid size={{ xs: 12, md: 6 }}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                        Payment Method Distribution
                      </Typography>
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={Object.entries(report.by_payment_method).map(([method, data]: [string, any]) => ({
                            method: method || 'Unknown',
                            count: data.count || 0,
                          }))}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke={colors.grey[200]} />
                          <XAxis dataKey="method" tick={{ fontSize: 12 }} />
                          <YAxis tick={{ fontSize: 12 }} />
                          <Tooltip />
                          <Bar dataKey="count" fill={colors.primary.main} radius={[8, 8, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>

            {/* Transactions DataGrid */}
            {report.transactions && report.transactions.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                    Transaction Details
                  </Typography>
                  <Box sx={{ height: 600, width: '100%' }}>
                    <DataGrid
                      rows={report.transactions.map((transaction: any, index: number) => ({
                        id: transaction.id || index,
                        ...transaction,
                      }))}
                      columns={[
                        {
                          field: 'transaction_reference',
                          headerName: 'Reference',
                          width: 180,
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
                              {params.value || 'N/A'}
                            </Typography>
                          ),
                        },
                        {
                          field: 'service',
                          headerName: 'Service',
                          width: 200,
                          valueGetter: (value, row) => row.service?.name || 'N/A',
                        },
                        {
                          field: 'amount',
                          headerName: 'Amount (KES)',
                          width: 140,
                          type: 'number',
                          renderCell: (params) => (
                            <Typography variant="body2" fontWeight={700} color={colors.primary.main}>
                              {params.value?.toLocaleString() || 0}
                            </Typography>
                          ),
                        },
                        {
                          field: 'payment_method',
                          headerName: 'Payment Method',
                          width: 150,
                          renderCell: (params) => (
                            <Chip
                              label={params.value || 'Unknown'}
                              size="small"
                              variant="outlined"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          ),
                        },
                        {
                          field: 'status',
                          headerName: 'Status',
                          width: 130,
                          renderCell: (params) => {
                            const icon = getStatusIcon(params.value);
                            return (
                              <Chip
                                {...(icon && { icon })}
                                label={params.value}
                                color={getStatusColor(params.value)}
                                size="small"
                                sx={{ textTransform: 'capitalize' }}
                              />
                            );
                          },
                        },
                        {
                          field: 'touchPoint',
                          headerName: 'Touchpoint',
                          width: 150,
                          valueGetter: (value, row) => row.touchPoint?.touch_point || 'N/A',
                        },
                        {
                          field: 'user',
                          headerName: 'User',
                          width: 150,
                          valueGetter: (value, row) =>
                            row.user ? `${row.user.first_name || ''} ${row.user.last_name || ''}`.trim() : 'N/A',
                        },
                        {
                          field: 'created_at',
                          headerName: 'Date',
                          width: 160,
                          renderCell: (params) => (
                            <Typography variant="body2">
                              {new Date(params.value).toLocaleString()}
                            </Typography>
                          ),
                        },
                      ] as GridColDef[]}
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{
                        toolbar: {
                          showQuickFilter: true,
                          quickFilterProps: { debounceMs: 500 },
                        },
                      }}
                      initialState={{
                        pagination: { paginationModel: { pageSize: 10 } },
                        sorting: {
                          sortModel: [{ field: 'created_at', sort: 'desc' }],
                        },
                      }}
                      pageSizeOptions={[5, 10, 25, 50, 100]}
                      disableRowSelectionOnClick
                      sx={{
                        '& .MuiDataGrid-cell': {
                          borderBottom: `1px solid ${colors.grey[200]}`,
                          display: 'flex',
                          alignItems: 'center',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: colors.grey[100],
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* No Data Message */}
            {(!report.transactions || report.transactions.length === 0) && (
              <Card>
                <CardContent>
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                    <Receipt sx={{ fontSize: 64, color: colors.grey[400], mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      No Transactions Found
                    </Typography>
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      There are no transactions for the selected filters.
                      <br />
                      Try adjusting your filters or date range.
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="center" alignItems="center" py={8}>
                <Typography color="text.secondary">
                  Select a date range to generate transactions report
                </Typography>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </AdminLayout>
  );
};
